import { motion } from 'framer-motion';
import { ArrowRight, Database, Search, MessageSquare, Layers, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const steps = [
  { icon: Database, num: '01', title: 'Connect', desc: 'Plug into 40+ databases, data lakes, and APIs with pre-built connectors. PostgreSQL, BigQuery, Snowflake, MongoDB, and more.', color: '#00d4ff' },
  { icon: Search, num: '02', title: 'Discover', desc: 'AI auto-profiles every schema, infers relationships, and builds a living semantic knowledge graph of your entire data estate.', color: '#3b82f6' },
  { icon: MessageSquare, num: '03', title: 'Query', desc: 'Ask questions in plain English. DQP translates to optimized SQL, returns explainable answers, and remembers context.', color: '#8b5cf6' },
];

const layers = [
  { name: 'UX Surfaces', desc: 'Chat, Canvas, Dashboards', color: '#00d4ff' },
  { name: 'Reasoning & Insights', desc: 'Anomaly Detection, AI Governance', color: '#22d3ee' },
  { name: 'Semantic Graph', desc: 'Knowledge Graph, Schema Registry', color: '#3b82f6' },
  { name: 'Query Orchestration', desc: 'NL-to-SQL, Federated Queries', color: '#6366f1' },
  { name: 'Execution Engine', desc: 'Query Optimization, Caching', color: '#8b5cf6' },
  { name: 'Data Sources', desc: '40+ Connectors, CDC Streams', color: '#a855f7' },
];

const integrations = [
  'PostgreSQL', 'MySQL', 'BigQuery', 'Snowflake', 'MongoDB', 'Redis',
  'Amazon S3', 'Redshift', 'Databricks', 'ClickHouse', 'Elasticsearch', 'Kafka',
];

const versionFeatures = [
  { version: 'V1 (Current)', features: ['Auto-Semantic Discovery', 'NL-to-SQL Chat', 'Insight Review', '40+ Connectors', 'Governance Registry'] },
  { version: 'V2', features: ['Visual Canvas', 'Data Wrangling', 'Advanced Pipelines', 'Custom Workflows', 'Team Collaboration'] },
  { version: 'V3', features: ['Native Visualizations', 'Embedded Analytics', 'Custom AI Models', 'Marketplace', 'White-label'] },
];

export default function Product() {
  return (
    <div className="bg-[#0a0a1a] min-h-screen">
      {/* Hero */}
      <section data-testid="product-hero" className="relative py-24 md:py-32">
        <div className="glow-spot w-[400px] h-[400px] bg-[#3b82f6] top-[-50px] right-[10%] opacity-10" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">Product</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              How <span className="gradient-text">DQP</span> Works
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-slate-400 max-w-2xl mx-auto">
              Three steps from raw data to actionable intelligence. No code required.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* 3-Step Flow */}
      <section data-testid="product-steps" className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="glass-card p-8 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-full h-1" style={{ background: `linear-gradient(90deg, ${s.color}, transparent)` }} />
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: `${s.color}15` }}>
                  <s.icon className="w-7 h-7" style={{ color: s.color }} />
                </div>
                <span className="text-sm font-bold block mb-2" style={{ color: s.color }}>Step {s.num}</span>
                <h3 className="text-white font-bold text-2xl mb-3" style={{ fontFamily: 'Outfit' }}>{s.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                {i < 2 && <ChevronRight className="hidden md:block absolute top-1/2 -right-4 w-6 h-6 text-white/20" />}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Architecture */}
      <section data-testid="product-architecture" className="py-24 md:py-32 bg-[#111122]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">Architecture</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Six layers of intelligence
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg max-w-2xl mx-auto">
              A purpose-built architecture designed for enterprise-scale data intelligence.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto space-y-3">
            {layers.map((layer, i) => (
              <motion.div key={i} variants={fadeUp} className="glass-card p-5 flex items-center justify-between group hover:border-white/20 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-3 h-3 rounded-full" style={{ background: layer.color }} />
                  <div>
                    <h4 className="text-white font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>{layer.name}</h4>
                    <p className="text-slate-500 text-xs">{layer.desc}</p>
                  </div>
                </div>
                <Layers className="w-4 h-4 text-slate-600 group-hover:text-white/40 transition-colors" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Version Matrix */}
      <section data-testid="product-versions" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">Roadmap</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>Capability Matrix</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {versionFeatures.map((v, i) => (
              <motion.div key={i} variants={fadeUp} className={`glass-card p-8 ${i === 0 ? 'border-[#00d4ff]/30' : ''}`}>
                <span className={`text-sm font-bold mb-4 block ${i === 0 ? 'text-[#00d4ff]' : 'text-slate-500'}`}>{v.version}</span>
                <ul className="space-y-3">
                  {v.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-3 text-sm">
                      <div className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-[#00d4ff]' : 'bg-slate-600'}`} />
                      <span className={i === 0 ? 'text-slate-300' : 'text-slate-500'}>{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Integrations */}
      <section data-testid="product-integrations" className="py-24 md:py-32 bg-[#111122]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Connects to everything
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mb-12">40+ connectors and growing. If you have data, DQP can reach it.</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              {integrations.map(name => (
                <span key={name} className="px-5 py-2.5 rounded-full border border-white/10 text-slate-400 text-sm bg-white/[0.02] hover:border-[#00d4ff]/30 hover:text-white transition-all cursor-default">
                  {name}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto px-6">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit' }}>
            See it in action
          </motion.h2>
          <motion.div variants={fadeUp} className="flex justify-center gap-4">
            <Link to="/signup">
              <Button className="bg-gradient-to-r from-[#00d4ff] to-[#3b82f6] text-[#0a0a1a] font-semibold rounded-full px-8 py-3 h-12 hover:opacity-90 shadow-[0_0_20px_rgba(0,212,255,0.3)]">
                Start Free Trial <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
